import { Box, Button, Collapse, Flex, FormLabel, FormControl, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikInput, FormikSelect, FormikSwitch } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import RadioInput from 'components/RadioCard';
import Title from 'components/Title';
import {
  dateFormatOptions,
  currencyOptions,
  autoLunchBreakOptions,
  lunchBreakDurationOptions,
} from 'containers/CompanySettings/shared/options';
import { MILES_IN_KILOMETERS } from 'global/appConstants';
import FEATURES from 'global/features';
import { PLANS } from 'global/plans';
import { useFetchSettings } from 'hooks/companySettings/useFetchSettings';
import { useFetchTimezones } from 'hooks/companySettings/useFetchTimezone';
import { useSaveGeneralSettings, ISaveGeneralSettings } from 'hooks/companySettings/useSaveGeneralSettings';
import { useFeatureList } from 'store/useFeatureStore';
import { useCompanyInfo } from 'store/useUserStore';
import { formatDecimalPlace, formatToTwoDecimalPlace } from 'utils/format';

import { initialGeneralSettingsData, generalSettingsValidation } from './schema';

const General: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'companySettings']);

  const featureList: any = useFeatureList();
  const companyInfo = useCompanyInfo();

  const [lunchDuration, setLunchDuration] = useState<any>();
  const [formData, setFormData] = useState<any>(null);

  const { data, status, refetch } = useFetchSettings('general');
  const { data: timezoneOptions, status: timezoneStatus } = useFetchTimezones();
  const { status: saveSettingsStatus, mutateAsync } = useSaveGeneralSettings();

  const isPremiumOrFreeTrial: boolean = useMemo(() => {
    return companyInfo?.plan?.toLowerCase() == PLANS.premium || companyInfo?.plan?.toLowerCase() === PLANS.freeTrial;
  }, [companyInfo]);

  useEffect(
    () => {
      if (status === 'success') {
        setLunchDuration(data?.payload?.lunch_break_duration.toString());
        const pFormData = {
          ...data?.payload,
          timezone: data?.payload?.timezone || null,
          mileage_cost: data?.payload?.mileage_in_km
            ? formatDecimalPlace(data?.payload?.mileage_cost / MILES_IN_KILOMETERS)
            : formatDecimalPlace(data?.payload?.mileage_cost),
          driving_speed: data?.payload?.mileage_in_km
            ? formatToTwoDecimalPlace(data?.payload?.driving_speed * MILES_IN_KILOMETERS)
            : formatToTwoDecimalPlace(data?.payload?.driving_speed),
          date_format: data?.payload?.date_format
            ? dateFormatOptions.find((item: any) => item.value === data?.payload?.date_format)
            : null,
          company_currency: data?.payload?.company_currency
            ? currencyOptions.find((item: any) => item.value === data?.payload?.company_currency)
            : null,
          lunch_break_automatically:
            data?.payload?.lunch_break_automatically && !data?.payload?.manual_breaks
              ? (autoLunchBreakOptions.find(
                  (item: any) => item.value === data?.payload?.lunch_break_automatically.toString()
                ) ?? { label: 'None', value: '0' })
              : { label: 'None', value: '0' },
          cali_break_policy: data?.payload?.cali_break_policy ? data?.payload?.cali_break_policy : false,
          show_signature_sign_off: data?.payload?.signatures ? data?.payload?.signatures : false,
          commuter_mileage: data?.payload?.commuter_mileage ?? false,
          has_evv: data?.payload?.evvSetting?.has_evv ?? false,
          allows_kiosk_app: data?.payload?.allows_kiosk_app ?? false,
          custom_fields: data?.payload?.custom_fields ?? false,
        };

        setFormData(pFormData);
      }
    }, // eslint-disable-next-line
    [status, timezoneStatus, setLunchDuration]
  );

  const handleToggle = (
    e: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    controlName: string
  ) => {
    setFieldValue(controlName, e.target.checked);
  };

  const handleWithOtherToggle = (
    e: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    controlName: string
  ) => {
    const currentValue = e.target.checked;
    setFieldValue(controlName, currentValue);

    if (controlName === 'segmented_tracking' && currentValue) {
      setFieldValue('allow_automatic_gps_clock_in', 0);
    }

    if (controlName === 'allow_automatic_gps_clock_in' && currentValue) {
      setFieldValue('segmented_tracking', 0);
    }

    if (controlName === 'cali_break_policy' && currentValue) {
      setFieldValue('manual_breaks', 1);
    }
  };

  const handleManualBreakToggle = (
    e: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    controlName: string
  ) => {
    const currentManualBreak = e.target.checked;
    setFieldValue(controlName, currentManualBreak);
    if (currentManualBreak) {
      setFieldValue('lunch_break_duration', '0');
      setLunchDuration('0');
      setFieldValue('lunch_break_automatically', { label: 'None', value: '0' });
    } else {
      setFieldValue('cali_break_policy', 0);
    }
  };

  const handleFormSubmit = async (values: typeof initialGeneralSettingsData) => {
    const postData: ISaveGeneralSettings = {
      company_name: values.company_name,
      allow_automatic_gps_clock_in: values.allow_automatic_gps_clock_in,
      face_recognition_clock_in: values.face_recognition_clock_in,
      job_clock_in: values.job_clock_in,
      task_clock_in: values.task_clock_in,
      mileage_in_km: values.mileage_in_km,
      time_off_in_menu: values.time_off_in_menu,
      split_time: values.split_time,
      new_user_password_reset: values.new_user_password_reset,
      geofence_clock_in: values.geofence_clock_in,
      block_website_clock_in: values.block_website_clock_in,
      manual_breaks: values.manual_breaks,
      mileage_clock_out: values.mileage_clock_out,
      timezone: typeof values.timezone === 'object' ? values.timezone.value : values.timezone,
      date_format: values.date_format.value,
      mileage_cost: values.mileage_in_km ? values.mileage_cost * MILES_IN_KILOMETERS : values.mileage_cost,
      driving_speed: values.mileage_in_km ? values.driving_speed / MILES_IN_KILOMETERS : values.driving_speed,
      company_currency: values.company_currency.value,
      lunch_break_automatically: values.lunch_break_automatically.value,
      lunch_break_duration: parseInt(lunchDuration),
      clock_out_gps_off: isPremiumOrFreeTrial ? values.clock_out_gps_off : false,
      clock_out_force_close_app: isPremiumOrFreeTrial ? values.clock_out_force_close_app : false,
      cali_break_policy: values.cali_break_policy ? true : false,
      show_signature_sign_off: values.show_signature_sign_off ? true : false,
      commuter_mileage: values.commuter_mileage ?? false,
      has_evv: values.has_evv ?? false,
      allows_kiosk_app: values.allows_kiosk_app ?? false,
      custom_fields: values.custom_fields ?? false,
    };

    mutateAsync(postData).then(() => {
      refetch();
    });
  };

  if (status === 'loading' || !formData || !lunchDuration) return <LogoLoader />;
  return (
    <>
      <Box mb={{ base: '2', md: '0' }}>
        <Title hideBackButton={true}> {t('companySettings:generalSettings')}</Title>
      </Box>
      <Box mt="26px">
        <Formik
          initialValues={formData}
          validationSchema={generalSettingsValidation}
          enableReinitialize
          onSubmit={handleFormSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Box display="flex" flexDirection="column" maxWidth="400px">
                <FormikInput
                  type="text"
                  name="company_name"
                  label={t('companySettings:companyName')}
                  formControlProps={{ isRequired: true }}
                />
                <FormikSelect
                  name="timezone"
                  label={t('companySettings:timezone')}
                  options={timezoneOptions}
                  formControlProps={{ isRequired: true }}
                  selectProps={{
                    placeholder: t('companySettings:selectTimezone'),
                    isClearable: false,
                    isLoading: timezoneStatus === 'loading',
                    value:
                      typeof values.timezone === 'object'
                        ? values.timezone
                        : (timezoneOptions || []).find((option: any) => option.value === values.timezone) || '',
                  }}
                />
                <FormikSelect
                  name="date_format"
                  label={t('companySettings:dateFormat')}
                  options={dateFormatOptions}
                  formControlProps={{ isRequired: true }}
                  selectProps={{
                    placeholder: t('companySettings:selectFormat'),
                    isClearable: false,
                  }}
                />
              </Box>

              <Collapse
                in={!values.manual_breaks}
                unmountOnExit={true}
                animateOpacity={false}
                style={{ willChange: 'initial', overflow: 'initial' }}
              >
                <Box maxWidth="400px">
                  <FormikSelect
                    name="lunch_break_automatically"
                    label={t('companySettings:insertLunchBreakAutomatically')}
                    options={autoLunchBreakOptions}
                    formControlProps={{ isRequired: true }}
                    selectProps={{
                      placeholder: t('common:select'),
                      isClearable: false,
                    }}
                  />
                </Box>
                <Flex mb="17px" direction="column">
                  <FormControl
                    isRequired={values.lunch_break_automatically.label !== 'None'}
                    isDisabled={values.lunch_break_automatically.label === 'None'}
                  >
                    <FormLabel
                      fontSize="xs"
                      color="gray.500"
                      fontWeight={600}
                      mb={1}
                      textTransform="uppercase"
                      sx={{
                        _disabled: {
                          cursor: 'not-allowed',
                        },
                      }}
                    >
                      {t('companySettings:lunckBreakDuration')}
                    </FormLabel>
                    <RadioInput
                      variant="primary"
                      options={lunchBreakDurationOptions}
                      name="lunch_break_duration"
                      defaultValue={lunchDuration}
                      handleChange={setLunchDuration}
                      isDisabled={values.lunch_break_automatically.label === 'None'}
                    ></RadioInput>
                  </FormControl>
                </Flex>
              </Collapse>

              <Box display="flex" flexDirection="column" maxWidth="390px">
                <FormikSelect
                  name="company_currency"
                  label={t('companySettings:companyCurrency')}
                  options={currencyOptions}
                  formControlProps={{ isRequired: true }}
                  selectProps={{
                    placeholder: t('common:select'),
                    isClearable: false,
                  }}
                />
                <FormikInput
                  type="text"
                  name="mileage_cost"
                  label={t('companySettings:mileageCost(Per', { milesOrKm: values.mileage_in_km ? 'KM' : 'MILE' })}
                  inputProps={{
                    placeholder: 'eg. 0.665',
                  }}
                />

                <FormikInput
                  type="text"
                  name="driving_speed"
                  label={t('companySettings:countMileageForSpeedOver', {
                    milesOrKm: values.mileage_in_km ? 'KM' : 'MILE',
                  })}
                />

                {/* Switches */}
                {/* <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:enablePasswordResetForNewUsers')}</FormLabel>
                  <FormikSwitch
                    name="new_user_password_reset"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'new_user_password_reset')}
                  ></FormikSwitch>
                </Box> */}

                {!(companyInfo?.plan?.toLowerCase() === PLANS.basic) && (
                  <React.Fragment>
                    <Box display="flex" justifyContent="space-between" mb="17px">
                      <FormLabel fontSize="sm">
                        {t('companySettings:allowAutomaticClockInOutBasedOnGpsLocation')}
                      </FormLabel>
                      <FormikSwitch
                        name="allow_automatic_gps_clock_in"
                        onChangeCallback={e => handleWithOtherToggle(e, setFieldValue, 'allow_automatic_gps_clock_in')}
                      ></FormikSwitch>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb="17px">
                      <FormLabel fontSize="sm" cursor="not-allowed" color="gray.500">
                        {t('companySettings:requireFaceRecognitionFOrUsersToClockIn')}
                      </FormLabel>
                      <FormikSwitch
                        name="face_recognition_clock_in"
                        switchProps={{
                          isDisabled: true,
                        }}
                        // onChangeCallback={e => handleToggle(e, setFieldValue, 'face_recognition_clock_in')}
                      ></FormikSwitch>
                    </Box>
                    {featureList[FEATURES.jobs] && (
                      <React.Fragment>
                        <Box display="flex" justifyContent="space-between" mb="17px">
                          <FormLabel fontSize="sm">
                            {t('companySettings:requireAllusersToSelectAJobToClockIn')}
                          </FormLabel>
                          <FormikSwitch
                            name="job_clock_in"
                            onChangeCallback={e => handleToggle(e, setFieldValue, 'job_clock_in')}
                          ></FormikSwitch>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb="17px">
                          <FormLabel fontSize="sm">
                            {t('companySettings:allowUsersToChooseTaskWhenClockingIn')}
                          </FormLabel>
                          <FormikSwitch
                            name="task_clock_in"
                            onChangeCallback={e => handleToggle(e, setFieldValue, 'task_clock_in')}
                          ></FormikSwitch>
                        </Box>
                      </React.Fragment>
                    )}
                    <Box display="flex" justifyContent="space-between" mb="17px">
                      <FormLabel fontSize="sm">
                        {t('companySettings:requireUsersToBeWithinGeofenceToClockIn')}
                      </FormLabel>
                      <FormikSwitch
                        name="geofence_clock_in"
                        onChangeCallback={e => handleToggle(e, setFieldValue, 'geofence_clock_in')}
                      ></FormikSwitch>
                    </Box>
                  </React.Fragment>
                )}

                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:blockUserFromClockinInThroughWebsite')}</FormLabel>
                  <FormikSwitch
                    name="block_website_clock_in"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'block_website_clock_in')}
                  ></FormikSwitch>
                </Box>

                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:showMileageInKilometers')}</FormLabel>
                  <FormikSwitch
                    name="mileage_in_km"
                    onChangeCallback={e => {
                      if (e.target.value === 'true') {
                        setFieldValue(
                          'mileage_cost',
                          formatToTwoDecimalPlace(values.mileage_cost * MILES_IN_KILOMETERS)
                        );
                        setFieldValue(
                          'driving_speed',
                          formatToTwoDecimalPlace(values.driving_speed / MILES_IN_KILOMETERS)
                        );
                      } else {
                        setFieldValue(
                          'mileage_cost',
                          formatToTwoDecimalPlace(values.mileage_cost / MILES_IN_KILOMETERS)
                        );
                        setFieldValue(
                          'driving_speed',
                          formatToTwoDecimalPlace(values.driving_speed * MILES_IN_KILOMETERS)
                        );
                      }
                    }}
                  ></FormikSwitch>
                </Box>

                {/* <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">
                    {t('companySettings:requireUsersToInputMileageBeforeClockingOut')}
                  </FormLabel>
                  <FormikSwitch
                    name="mileage_clock_out"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'mileage_clock_out')}
                  ></FormikSwitch>
                </Box> */}

                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:allowUsersToTakeBreaksManually')}</FormLabel>
                  <FormikSwitch
                    name="manual_breaks"
                    onChangeCallback={e => handleManualBreakToggle(e, setFieldValue, 'manual_breaks')}
                  ></FormikSwitch>
                </Box>

                {/* {!(companyInfo?.plan?.toLowerCase() === PLANS.basic) && (
                  <Box display="flex" justifyContent="space-between" mb="17px">
                    <FormLabel fontSize="sm">{t('companySettings:showTimeOffInMenu')}</FormLabel>
                    <FormikSwitch
                      name="time_off_in_menu"
                      onChangeCallback={e => handleToggle(e, setFieldValue, 'time_off_in_menu')}
                    ></FormikSwitch>
                  </Box>
                )} */}

                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:splitTimeAtMidnight')}</FormLabel>
                  <FormikSwitch
                    name="split_time"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'split_time')}
                  ></FormikSwitch>
                </Box>

                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm" cursor={isPremiumOrFreeTrial ? 'auto' : 'not-allowed'}>
                    {t('companySettings:clockOutUsersWhenTheyTurnOffTheirGPS')}
                    {!isPremiumOrFreeTrial && (
                      <Text fontSize="xs" color="secondary.700">
                        ({t('common:premium')})
                      </Text>
                    )}
                  </FormLabel>
                  <FormikSwitch
                    name="clock_out_gps_off"
                    switchProps={{
                      isDisabled: !isPremiumOrFreeTrial,
                    }}
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'clock_out_gps_off')}
                  ></FormikSwitch>
                </Box>

                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm" cursor={isPremiumOrFreeTrial ? 'auto' : 'not-allowed'}>
                    {t('companySettings:clockOutUsersWhenForceClosingApp')}
                    {!isPremiumOrFreeTrial && (
                      <Text fontSize="xs" color="secondary.700">
                        ({t('common:premium')})
                      </Text>
                    )}
                  </FormLabel>
                  <FormikSwitch
                    name="clock_out_force_close_app"
                    switchProps={{
                      isDisabled: !isPremiumOrFreeTrial,
                    }}
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'clock_out_force_close_app')}
                  ></FormikSwitch>
                </Box>

                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">
                    {t('companySettings:californiaBreakPolicy')}
                    {companyInfo?.plan?.toLowerCase() === PLANS.basic && (
                      <Text fontSize="xs" color="secondary.700">
                        ({t('common:pro')} {t('common:or')} {t('common:premium')})
                      </Text>
                    )}
                  </FormLabel>
                  <FormikSwitch
                    name="cali_break_policy"
                    switchProps={{
                      isDisabled: companyInfo?.plan?.toLowerCase() === PLANS.basic,
                    }}
                    onChangeCallback={e => handleWithOtherToggle(e, setFieldValue, 'cali_break_policy')}
                  ></FormikSwitch>
                </Box>

                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">
                    {t('companySettings:showSignatures')}
                    {!isPremiumOrFreeTrial && (
                      <Text fontSize="xs" color="secondary.700">
                        ({t('common:premium')})
                      </Text>
                    )}
                  </FormLabel>
                  <FormikSwitch
                    name="show_signature_sign_off"
                    switchProps={{
                      isDisabled: !isPremiumOrFreeTrial,
                    }}
                    onChangeCallback={e => handleWithOtherToggle(e, setFieldValue, 'show_signature_sign_off')}
                  ></FormikSwitch>
                </Box>

                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">
                    {t('companySettings:enableCommuteMileage')}
                    {!isPremiumOrFreeTrial && (
                      <Text fontSize="xs" color="secondary.700">
                        ({t('common:premium')})
                      </Text>
                    )}
                  </FormLabel>
                  <FormikSwitch
                    name="commuter_mileage"
                    switchProps={{
                      isDisabled: !isPremiumOrFreeTrial,
                    }}
                    onChangeCallback={e => handleWithOtherToggle(e, setFieldValue, 'commuter_mileage')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">
                    {t('companySettings:enableEVV')}
                    {!isPremiumOrFreeTrial && (
                      <Text fontSize="xs" color="secondary.700">
                        ({t('common:premium')})
                      </Text>
                    )}
                  </FormLabel>
                  <FormikSwitch
                    name="has_evv"
                    switchProps={{
                      // isDisabled: !isPremiumOrFreeTrial,
                      isDisabled: true,
                    }}
                    onChangeCallback={e => handleWithOtherToggle(e, setFieldValue, 'has_evv')}
                  ></FormikSwitch>
                </Box>
                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">
                    {t('companySettings:enableKioskApp')}
                    {/* {!isPremiumOrFreeTrial && (
                      <Text fontSize="xs" color="secondary.700">
                        ({t('common:premium')})
                      </Text>
                    )} */}
                  </FormLabel>
                  <FormikSwitch
                    name="allows_kiosk_app"
                    onChangeCallback={e => handleWithOtherToggle(e, setFieldValue, 'allows_kiosk_app')}
                  ></FormikSwitch>
                </Box>

                <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">
                    {t('companySettings:enableCustomFields')}
                    {!isPremiumOrFreeTrial && (
                      <Text fontSize="xs" color="secondary.700">
                        ({t('common:premium')})
                      </Text>
                    )}
                  </FormLabel>
                  <FormikSwitch
                    name="custom_fields"
                    switchProps={{
                      isDisabled: true,
                    }}
                    onChangeCallback={e => handleWithOtherToggle(e, setFieldValue, 'custom_fields')}
                  ></FormikSwitch>
                </Box>
              </Box>

              <Button
                type="submit"
                colorScheme="secondary"
                isLoading={saveSettingsStatus === 'loading'}
                minWidth="100px"
              >
                {t('common:save')}
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};
export default General;
